import { createI18n } from 'vue-i18n'
import { App, watch } from 'vue'
import en from '../locales/en.json'
import fr from '../locales/fr.json'
import { en as primeEn } from 'primelocale/js/en.js'
import { fr as primeFr } from 'primelocale/js/fr.js'

type GetBrowserLocalesOptions = {
    countryCodeOnly?: boolean
}

function getBrowserLocales(options: GetBrowserLocalesOptions = {}): string[] {
    const defaultOptions: Required<GetBrowserLocalesOptions> = { countryCodeOnly: false }
    const opt = { ...defaultOptions, ...options }
    const locales: string[] = []
    if (typeof navigator !== 'undefined') {
        if (navigator.languages === undefined) {
            if ((navigator as any).language) {
                locales.push((navigator as any).language as string)
            }
        } else {
            ; (navigator.languages as readonly string[]).forEach((language) => locales.push(language))
        }
    }
    return locales.map((locale) => (opt.countryCodeOnly ? locale.trim().split(/-|_/)[0] : locale.trim()))
}

/**
 * Keep only tags every Intl API will accept (MYAC-1002 e2e finding).
 *
 * The active vue-i18n locale is fed raw into `Intl.DateTimeFormat` (the `d()`
 * datetime formatter) and `String.prototype.localeCompare` — and browsers on
 * POSIX-locale hosts can report tags like `en-US@posix`, which are NOT valid
 * BCP-47 and make every one of those calls throw `RangeError`, blanking whole
 * views. Salvage the base tag before the `@` when possible, validate via
 * `Intl.getCanonicalLocales` (it throws on invalid tags), drop what's left.
 */
export function sanitizeLocaleTags(tags: string[]): string[] {
    const out: string[] = []
    for (const raw of tags) {
        const candidate = raw.split('@')[0].trim()
        if (!candidate) continue
        try {
            Intl.getCanonicalLocales(candidate)
            if (!out.includes(candidate)) out.push(candidate)
        } catch {
            // invalid tag — drop it, the fallback chain ends in 'en'
        }
    }
    return out
}

function loadLocaleMessages() {
    return { en, fr }
}

const browserLocales = sanitizeLocaleTags(getBrowserLocales({ countryCodeOnly: false }))
const localeMessages = loadLocaleMessages()

// The interface language the person chose in their profile (MYAC-1522),
// remembered on this browser so the next load starts in it instead of flashing
// the browser's. Their user doc carries it to other browsers (stores/users.ts).
const INTERFACE_LANGUAGE_KEY = 'mac.interfaceLanguage'

function readStoredInterfaceLanguage(): 'fr' | 'en' | null {
    try {
        const v = localStorage.getItem(INTERFACE_LANGUAGE_KEY)
        return v === 'fr' || v === 'en' ? v : null
    } catch {
        return null
    }
}

const browserLocale: string = browserLocales.length > 0 ? browserLocales[0] : (import.meta as any).env.VITE_I18N_LOCALE || 'en'

/** The browser's own tags: what "the browser's language" means in the profile. */
export function browserLanguageTags(): string[] {
    return [...browserLocales]
}

const i18n = createI18n({
    legacy: false,
    locale: readStoredInterfaceLanguage() ?? browserLocale,
    fallbackLocale: [
        ...browserLocales,
        (import.meta as any).env.VITE_I18N_FALLBACK_LOCALE || 'en',
        'en',
    ],
    silentFallbackWarn: true as any,
    messages: localeMessages,
})

// Set datetime formats only for the current locale (and its base language)
function applyDateTimeFormatsForCurrentLocale() {
    const localeRef: any = (i18n.global as any).locale
    const currentLocale: string = typeof localeRef === 'string' ? localeRef : localeRef?.value || 'en'
    const baseLang = currentLocale.split('-')[0]
    const formats = baseLang === 'fr' ? (fr as any).datetimeFormats : (en as any).datetimeFormats
    ;(i18n.global as any).setDateTimeFormat(baseLang, formats)
    ;(i18n.global as any).setDateTimeFormat(currentLocale, formats)
}

applyDateTimeFormatsForCurrentLocale()

/**
 * Switch the interface to the person's chosen language, or back to the
 * browser's (`null`). The locale watcher below re-syncs PrimeVue and the date
 * formats.
 */
export function setInterfaceLanguage(language: 'fr' | 'en' | null): void {
    try {
        if (language) localStorage.setItem(INTERFACE_LANGUAGE_KEY, language)
        else localStorage.removeItem(INTERFACE_LANGUAGE_KEY)
    } catch { /* storage refused: this load only */ }
    const next = language ?? browserLocale
    // Composition mode (legacy: false): `locale` is a ref.
    const localeRef = (i18n.global as unknown as { locale: { value: string } }).locale
    if (localeRef.value !== next) localeRef.value = next
}

export function initPrimeVueLocaleSync(app: App) {
    const primevue = (app.config.globalProperties as any).$primevue

    const getPrimeLocale = () => {
        const language = i18n.global.t('language') as string
        if (language === 'FR') {
            return primeFr
        }
        return primeEn
    }

    if (primevue?.config) {
        primevue.config.locale = getPrimeLocale()
    }

    // Update PrimeVue locale and datetime formats when language changes
    const localeRef: any = (i18n.global as any).locale
    watch(localeRef, () => {
        if (primevue?.config) {
            primevue.config.locale = getPrimeLocale()
        }
        applyDateTimeFormatsForCurrentLocale()
    })
}

export default i18n


